import { Credentials as _Credentials, Caveat, Columns } from '#~/common/editor/index.js';
import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_NAMES, EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { applyCredentialsToCandidate } from './utils.js';
import { resolveDeviceAnalyticsCatalogContext } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';

interface CredentialsProps extends HtmlAttributes {
  error?: string | null;
  onErrorChange?: (error: string | null) => void;
  onAuthenticationStart?: () => void;
  // `canSkip` is true only for the known-model INVALID verdict; `onSkip` (owned
  // by discovery.tsx) flips authSkipped and closes the modal.
  canSkip?: boolean;
  onSkip?: () => void;
  // Lockout hint shown under the error (discovery.tsx resolveLockoutHint). While
  // set, the Skip button reads "Continue anyway": the camera may be locking out
  // logins, so proceeding is a reasonable choice, not a dismissal.
  notice?: string | null;
}

/** Onvif discovery `<Credentials>`. */
export const Credentials = ({
  error,
  onErrorChange,
  onAuthenticationStart,
  canSkip,
  onSkip,
  notice,
  ...props
}: CredentialsProps): ReactElement => {
  const producer = useProducer();

  const { previews, sourceSelection } = useStore(({ previews, source }) => ({
    previews,
    sourceSelection: source.selection
  }));
  const roomId = useStore((µ) => µ.roomId);
  const { track } = useAnalytics();
  const authAnalyticsProps = {
    studio_id: roomId,
    // Stamp add_mode from whether the modal was entered via the known-model flow.
    // Per rule: stay-in-scope (ai.knowledge/workflow) — additive only.
    add_mode: (sourceSelection?.modelId ? 'one_click' : 'manual') as 'one_click' | 'manual',
    ...(sourceSelection?.modelId
      ? resolveDeviceAnalyticsCatalogContext(sourceSelection.modelId)
      : {}),
    ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection))
  };

  const [credentials, setCredentials] = useState<M_Credentials>();

  // Clear error when user starts typing new credentials
  useEffect(() => {
    if (credentials && onErrorChange) {
      onErrorChange(null);
    }
  }, [credentials, onErrorChange]);

  const handleLogin = useLatestFn(() => {
    // Signal that authentication is starting
    if (onAuthenticationStart) {
      onAuthenticationStart();
    }

    // Clear any existing error when attempting authentication
    if (onErrorChange) {
      onErrorChange(null);
    }

    producer(({ candidate }) => {
      // Analytics first; deviceType is hardcoded 'onvif' for both auth paths.
      if (previews.control) {
        const { error } = previews.control;
        if (error) {
          track(EVENT_NAMES.DEVICE_AUTH_INVALID, EVENT_CATEGORIES.DEVICE, {
            errorMessage: error.message,
            deviceType: 'onvif',
            ...authAnalyticsProps
          });
        }

        track(EVENT_NAMES.DEVICE_AUTHENTICATE, EVENT_CATEGORIES.DEVICE, {
          deviceType: 'onvif',
          ...authAnalyticsProps
        });
      }

      // Fold credentials into the candidate's control/video slots.
      applyCredentialsToCandidate(candidate, credentials);
    });
  });

  return (
    <Columns.Column {...props}>
      <Caveat title='Some cameras may require authentication to access their video stream or control interface.' />

      <div className='space-y-24'>
        <_Credentials credentials={toAccessor(credentials, setCredentials)} />

        {error && (
          <div className='flex items-center gap-8 rounded-8 bg-error/10 px-12 py-8 text-error'>
            <Icon className='text-md' icon='alert-circle' />
            <span className='text-sm'>{error}</span>
          </div>
        )}

        {/* Lockout hint: explains a possibly-false "Invalid" (see AUTH_LOCKOUT_HINT). */}
        {notice && (
          <div
            role='status'
            className='text-base-content/80 flex items-center gap-8 rounded-8 bg-base-300 px-12 py-8'
          >
            <Icon className='text-md' icon='info' />
            <span className='text-sm'>{notice}</span>
          </div>
        )}
      </div>

      {/* Skip button, shown only for the known-model INVALID verdict (canSkip). */}
      <div className='flex justify-end gap-8'>
        {canSkip && onSkip && (
          <Button variant='secondary' onClick={onSkip}>
            {notice ? 'Continue anyway' : 'Skip'}
          </Button>
        )}
        <Button disabled={!credentials?.username} onClick={handleLogin}>
          Authenticate
        </Button>
      </div>
    </Columns.Column>
  );
};
